import React from 'react';
import { useSelector } from 'react-redux';
import cls from 'classnames';

import { Text, Tag, TagSize, TextButtonPriority } from 'wix-ui-tpa';
import { useBi, useTranslation, useEnvironment } from '@wix/yoshi-flow-editor';
import { groupFeedTopicsSelectTopicInFeed } from '@wix/bi-logger-groups/v2';

import {
  selectAllTopics,
  selectTopicsState,
  selectFeedPermissionsByGroup,
} from 'store/selectors';
import { EFilterKeys } from 'api/feed/constants';

import { useDidUpdate } from 'common/hooks';
import { useCurrentStateAndParams } from 'common/router';
import { useController } from 'common/context/controller';
import { Box } from 'common/components/Box/Box';
import { BIUserEntry } from 'common/bi-logger/types';
import { CustomTextButton } from 'common/components/CustomTextButton';
import { skeleton } from 'common/components/Skeleton';
import { TopicLink } from 'common/components/TopicLink';

import * as discussionStyles from '../DiscussionPage.st.css';
import { st, classes } from './GroupTopics.st.css';

export interface IGroupTopicsProps {
  groupId: string;
}

export function GroupTopics(props: IGroupTopicsProps) {
  const { groupId } = props;

  const { t } = useTranslation();
  const { topics$ } = useController();
  const { isMobile } = useEnvironment();
  const { params } = useCurrentStateAndParams();

  const topics = useSelector(selectAllTopics);
  const topicState = useSelector(selectTopicsState);
  const permissions = useSelector(selectFeedPermissionsByGroup(groupId));

  const [collapsed, setCollapsed] = React.useState(!isMobile);
  const MAX_TO_SHOW = React.useMemo(() => (isMobile ? 4 : 10), [isMobile]);
  const activeId = params[EFilterKeys.TOPICS];

  const bi = useBi();

  useDidUpdate(() => {
    if (permissions.canViewPosts) {
      topics$.fetch(groupId);
    }
  }, [permissions.canViewPosts]);

  if (topicState.statuses.fetch.pending) {
    return <GroupTopicsSkeleton />;
  }

  if (!topics.length || !permissions.canViewPosts) {
    return null;
  }

  const topicsToShow = collapsed ? topics.slice(0, MAX_TO_SHOW) : topics;

  return (
    <Box
      article
      withSideBorders={!isMobile}
      className={st(classes.root, { isMobile })}
    >
      <Text className={discussionStyles.classes.widgetHeader}>
        {t('groups-web.discussion.topics.widget.title')}
      </Text>
      <ul className={classes.list}>
        {topicsToShow.map((topic) => (
          <li className={classes.item} key={topic.id}>
            <TopicLink
              withCounter
              groupId={groupId}
              topic={topic}
              onClick={handleTopicClick}
              active={topic.id === activeId}
            />
          </li>
        ))}
      </ul>
      {isMobile ? null : topics.length > MAX_TO_SHOW ? (
        <CustomTextButton
          priority={TextButtonPriority.primary}
          onClick={toggleCollapsed}
        >
          {collapsed
            ? t('groups-web.discussion.topics.widget.more')
            : t('groups-web.discussion.topics.widget.less')}
        </CustomTextButton>
      ) : null}
    </Box>
  );

  function toggleCollapsed() {
    setCollapsed(!collapsed);
  }

  function handleTopicClick() {
    bi.report(
      groupFeedTopicsSelectTopicInFeed({
        groupId: groupId as string,
        origin: 'feed_topics_section',
        userEntry: BIUserEntry.SITE,
      }),
    );
  }
}

function GroupTopicsSkeleton() {
  const { t } = useTranslation();

  return (
    <Box article>
      <Text className={discussionStyles.classes.widgetHeader}>
        {t('groups-web.discussion.topics.widget.title')}
      </Text>
      <ul className={classes.list}>
        <a className={classes.item}>
          <Tag className={cls(classes.tag, skeleton)} size={TagSize.small}>
            Lorem, ipsum.
          </Tag>
        </a>
        <a className={classes.item}>
          <Tag className={cls(classes.tag, skeleton)} size={TagSize.small}>
            lorem
          </Tag>
        </a>
        <a className={classes.item}>
          <Tag className={cls(classes.tag, skeleton)} size={TagSize.small}>
            lorem 3
          </Tag>
        </a>
        <a className={classes.item}>
          <Tag className={cls(classes.tag, skeleton)} size={TagSize.small}>
            Lorem ipsum dolor.
          </Tag>
        </a>
      </ul>
    </Box>
  );
}
